import {
  useState,
  useEffect,
  ReactNode,
  cloneElement,
  ReactElement,
  JSXElementConstructor,
  useRef,
} from "react";

interface ISlider {
  slides?: ReactNode[] | undefined;
  autoSlide?: boolean | undefined;
  autoSlideInterval?: number | undefined;
  showDots?: boolean | undefined;
  showArrows?: boolean | undefined;
  customDot?: (
    index: number,
  ) => ReactElement<any, string | JSXElementConstructor<any>>;
  getCurrentIndex?: (currentIndex: number) => void;
}

const Slider: React.FC<ISlider> = ({
  slides = [], // Default slides array
  autoSlide = true, // Enable or disable auto-sliding
  autoSlideInterval = 5000, // Default auto-slide interval
  showDots = true, // Show or hide dots
  showArrows = false,
  customDot,
  getCurrentIndex,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);

  const sliderRef = useRef(null);

  // Auto-sliding logic
  useEffect(() => {
    if (autoSlide && !isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          prevIndex === slides.length - 1 ? 0 : prevIndex + 1,
        );
      }, autoSlideInterval);

      return () => clearInterval(interval);
    }
  }, [autoSlide, autoSlideInterval, isHovered, slides.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1,
    );
  };

  // Drag handlers
  const handleTouchStart = (e: any) => {
    setIsDragging(true);
    setStartX(e.touches ? e.touches[0].clientX : e.clientX);
  };

  const handleTouchMove = (e: any) => {
    if (!isDragging) return;

    const currentX = e.touches ? e.touches[0].clientX : e.clientX;
    const diffX = currentX - startX;

    setTranslateX(diffX); // Visually move the slide while dragging
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsHovered(false);

    if (translateX > 50) {
      prevSlide(); // Swipe right
    } else if (translateX < -50) {
      nextSlide(); // Swipe left
    }

    setTranslateX(0); // Reset translateX
  };

  useEffect(() => {
    getCurrentIndex && getCurrentIndex(currentIndex);
  }, [currentIndex]);

  return (
    <div
      className="relative w-full max-w-3xl overflow-hidden max-xl:!mx-auto"
      onMouseEnter={() => setIsHovered(true)}
      //   onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleTouchStart}
      onMouseMove={handleTouchMove}
      onMouseUp={handleTouchEnd}
      onMouseLeave={handleTouchEnd}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      ref={sliderRef}
    >
      {/* Slides Container */}
      <div
        className="flex transition-transform duration-500"
        style={{
          direction: "ltr",
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {slides.map((slide, index) => (
          <div key={index} className="min-w-full !select-none">
            {slide}
          </div>
        ))}
      </div>

      {/* Navigation Buttons */}
      {showArrows && (
        <>
          <button
            className="absolute start-4 top-1/2 -translate-y-1/2 transform rounded-full bg-gray-700 p-2 text-white shadow-md hover:bg-gray-800"
            onClick={prevSlide}
          >
            &#10094;
          </button>
          <button
            className="absolute end-4 top-1/2 -translate-y-1/2 transform rounded-full bg-gray-700 p-2 text-white shadow-md hover:bg-gray-800"
            onClick={nextSlide}
          >
            &#10095;
          </button>
        </>
      )}

      {/* Dots Navigation */}
      {showDots && (
        <div className="mt-2 flex justify-center gap-1.5">
          {slides.map((_, index) =>
            customDot ? (
              cloneElement(customDot(index), {
                onClick: () => setCurrentIndex(index),
                key: index,
              })
            ) : (
              <button
                key={index}
                className={`h-3 w-3 rounded-full ${
                  index === currentIndex ? "bg-gray-800" : "bg-gray-400"
                }`}
                onClick={() => setCurrentIndex(index)}
              ></button>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default Slider;
